import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../apiService';
import { useLiveRefresh } from '../liveRefresh';

const TYPE_LABELS = {
  event_ticket: '🎫 Billet', delivery_order: '🛍 Livraison', lodging: '🏨 Hôtel',
  vehicle_rental: '🚗 Location', beauty_booking: '💇 Beauté',
  balance_topup: '💰 Recharge (client)', driver_balance_topup: '💰 Recharge (chauffeur)',
  supplier_balance_topup: '💰 Recharge (fournisseur)',
};
const typeLabel = (p) => TYPE_LABELS[p.plan_type] || p.plan_name || p.plan_type || '—';

const STATUS_META = {
  completed: { label: 'Complété', bg: '#ECFDF5', color: '#166534' },
  success: { label: 'Complété', bg: '#ECFDF5', color: '#166534' },
  pending: { label: 'En attente', bg: '#fef3c7', color: '#C9350F' },
  processing: { label: 'En cours', bg: '#FEE9E2', color: '#C9350F' },
  failed: { label: 'Échoué', bg: '#fee2e2', color: '#991b1b' },
  refunded: { label: 'Remboursé', bg: '#F4F5F7', color: '#414852' },
};

export default function PaymentsPage() {
  const [paymentsData, setPaymentsData] = useState([]);
  const [total, setTotal] = useState(0);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState('');
  const [status, setStatus] = useState('');
  const [planType, setPlanType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const loadPayments = useCallback(() => {
    setPaymentsLoading(true); setPaymentsError('');
    const params = new URLSearchParams({ limit: '200' });
    if (status) params.set('status', status);
    if (planType) params.set('plan_type', planType);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    apiService.get(`/admin/payments?${params.toString()}`, { userType: 'admin' })
      .then(res => {
        if (res.success) { setPaymentsData(res.payments || res.data || []); setTotal(res.total || (res.payments || []).length); }
        else setPaymentsError(res.error || 'Erreur lors du chargement des paiements');
      })
      .catch(err => setPaymentsError(err.message || 'Impossible de charger les paiements'))
      .finally(() => setPaymentsLoading(false));
  }, [status, planType, from, to]);

  useEffect(() => { loadPayments(); }, [loadPayments]);
  useLiveRefresh(() => loadPayments(), ['payment_received', 'balance_credited', 'balance_failed', 'topup', 'refund']);

  const reset = () => { setStatus(''); setPlanType(''); setFrom(''); setTo(''); };

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <h1 className="page-title">Historique des Paiements</h1>
        <div className="page-actions">
          <button className="btn-secondary" onClick={loadPayments}>🔄 Actualiser</button>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select className="filter-select" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="completed">Complété</option>
          <option value="processing">En cours</option>
          <option value="pending">En attente</option>
          <option value="failed">Échoué</option>
          <option value="refunded">Remboursé</option>
        </select>
        <select className="filter-select" value={planType} onChange={e => setPlanType(e.target.value)}>
          <option value="">Tous les types</option>
          <option value="event_ticket">🎫 Billets</option>
          <option value="delivery_order">🛍 Livraison</option>
          <option value="lodging">🏨 Hôtel</option>
          <option value="vehicle_rental">🚗 Location</option>
          <option value="beauty_booking">💇 Beauté</option>
          <option value="balance_topup">💰 Recharge client</option>
          <option value="driver_balance_topup">💰 Recharge chauffeur</option>
          <option value="supplier_balance_topup">💰 Recharge fournisseur</option>
        </select>
        <label style={{ fontSize: '0.85rem', color: '#5C636E' }}>Du <input type="date" className="filter-select" value={from} onChange={e => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: '0.85rem', color: '#5C636E' }}>Au <input type="date" className="filter-select" value={to} onChange={e => setTo(e.target.value)} /></label>
        {(status || planType || from || to) && <button className="btn-secondary" onClick={reset}>Réinitialiser</button>}
        <span style={{ marginLeft: 'auto', color: '#5C636E', fontSize: '0.9rem' }}>{total} paiement(s)</span>
      </div>

      {paymentsError && (
        <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '1rem', background: '#fef2f2', color: '#dc2626', fontWeight: '600' }}>{paymentsError}</div>
      )}

      <div className="table-container">
        {paymentsLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#5C636E' }}>
            <div className="spinner" style={{ margin: '0 auto 1rem' }}></div><p>Chargement des paiements...</p>
          </div>
        ) : paymentsData.length === 0 && !paymentsError ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#5C636E' }}>Aucun paiement pour ces filtres.</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Client</th><th>Type</th><th>Montant</th><th>Méthode</th><th>Date</th><th>Statut</th></tr>
            </thead>
            <tbody>
              {paymentsData.map((payment, idx) => {
                const st = STATUS_META[payment.status] || { label: payment.status || '—', bg: '#F4F5F7', color: '#414852' };
                const dt = payment.created_at || payment.date;
                return (
                  <tr key={payment.payment_id || payment._id || idx}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{payment.client || payment.client_name || '—'}</div>
                      {payment.client_phone && <div style={{ fontSize: '0.78rem', color: '#9AA0A8' }}>{payment.client_phone}</div>}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{typeLabel(payment)}</td>
                    <td className="amount">{typeof payment.amount === 'number' ? payment.amount.toLocaleString('fr-FR') + ' FC' : payment.amount || '—'}</td>
                    <td style={{ fontSize: '0.85rem', color: '#414852' }}>{payment.payment_method || payment.method || '—'}</td>
                    <td style={{ fontSize: '0.85rem' }}>{dt ? new Date(dt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    <td><span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700 }}>{st.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
